import { EVENTS } from '../constants/events';

interface GTMConfig {
  containerId: string;
  options?: {
    debug?: boolean;
    preview?: string;
    auth?: string;
    test?: string;
  };
}

// Interface para contexto do merchant
export interface MerchantContext {
  merchant_id?: string;
  merchant_slug?: string;
  store_name?: string;
  channel?: 'app' | 'pdv' | 'mesas' | 'retirada' | 'delivery';
}

export default class GTMPlugin {
  public name = 'google-tag-manager';
  public config: GTMConfig;
  private isInitialized = false;
  private isLoaded = false;
  [key: string]: unknown;

  constructor(
    config: GTMConfig = {
      containerId: '',
      options: { debug: false },
    }
  ) {
    this.config = config;
  }

  async initialize({ config }: { config: GTMConfig }) {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    this.config = { ...this.config, ...config };

    if (!this.config.containerId) {
      console.warn('GTM: Container ID não fornecido');
      return Promise.resolve();
    }

    // Inicializar dataLayer se não existir
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    console.log('GTM Plugin: Inicializando com Container ID:', this.config.containerId);

    // Marcar como inicializado - o GTMInjector vai cuidar do carregamento
    this.isInitialized = true;
    this.isLoaded = true; // Assumir que está carregado se foi injetado pelo GTMInjector

    return Promise.resolve();
  }

  page() {
    if (!this.isInitialized) {
      console.warn('GTM: Plugin não foi inicializado');
      return Promise.resolve();
    }

    // Enviar evento de page view
    this.pushToDataLayer({
      event: 'page_view',
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
    });

    return Promise.resolve();
  }

  track({ payload }: { payload: { event: string; properties?: Record<string, unknown> } }) {
    if (!this.isInitialized) {
      console.warn('GTM: Plugin não foi inicializado');
      return Promise.resolve();
    }

    const { event, properties } = payload;

    // Mapear eventos para o formato GTM
    let gtmEvent = event;
    let gtmProperties = { ...properties };

    switch (event) {
      case EVENTS.PAGE_VIEW:
        gtmEvent = 'page_view';
        gtmProperties = {
          page_title: document.title,
          page_location: window.location.href,
          page_path: window.location.pathname,
          ...properties,
        };
        break;

      case EVENTS.VIEW_CONTENT:
        gtmEvent = 'select_item';
        gtmProperties = {
          items: this.transformItems(properties?.contents),
          value: properties?.value,
          currency: properties?.currency || 'BRL',
          ...properties,
        };
        break;

      case EVENTS.ADD_TO_CART:
        gtmEvent = 'add_to_cart';
        gtmProperties = {
          items: this.transformItems(properties?.contents),
          value: properties?.value,
          currency: properties?.currency || 'BRL',
          ...properties,
        };
        break;

      case EVENTS.INITIATE_CHECKOUT:
        gtmEvent = 'begin_checkout';
        gtmProperties = {
          items: this.transformItems(properties?.contents),
          value: properties?.value,
          currency: properties?.currency || 'BRL',
          coupon: properties?.coupon,
          discount: properties?.discount,
          ...properties,
        };
        break;

      case EVENTS.ADD_PAYMENT_INFO:
        gtmEvent = 'add_payment_info';
        gtmProperties = {
          items: this.transformItems(properties?.contents),
          value: properties?.value,
          currency: properties?.currency || 'BRL',
          payment_type: properties?.payment_method,
          coupon: properties?.coupon,
          discount: properties?.discount,
          shipping: properties?.shipping,
          ...properties,
        };
        break;

      case EVENTS.PURCHASE:
        gtmEvent = 'purchase';
        gtmProperties = {
          transaction_id: properties?.order_id,
          items: this.transformItems(properties?.contents),
          value: properties?.value,
          currency: properties?.currency || 'BRL',
          coupon: properties?.coupon,
          discount: properties?.discount,
          shipping: properties?.shipping,
          ...properties,
        };
        break;

      // Eventos de status de pedido
      case EVENTS.ORDER_CREATED:
      case EVENTS.ORDER_ACCEPTED:
      case EVENTS.ORDER_OUT_FOR_DELIVERY:
      case EVENTS.ORDER_READY:
      case EVENTS.ORDER_DELIVERED:
      case EVENTS.ORDER_CANCELED:
        gtmEvent = event;
        gtmProperties = {
          order_id: properties?.order_id,
          value: properties?.value,
          currency: properties?.currency || 'BRL',
          ...properties,
        };
        break;

      default:
        // Para outros eventos, usar como estão
        break;
    }

    // Enviar evento para o GTM
    this.pushToDataLayer({
      event: gtmEvent,
      ...gtmProperties,
    });

    return Promise.resolve();
  }

  identify({ userId }: { userId: string }) {
    if (!this.isInitialized) {
      console.warn('GTM: Plugin não foi inicializado');
      return Promise.resolve();
    }

    // Enviar evento de identificação
    this.pushToDataLayer({
      event: 'user_identify',
      user_id: userId,
    });

    return Promise.resolve();
  }

  // Método para transformar items para o formato GTM
  private transformItems(contents?: unknown): Array<{
    item_id: string;
    item_name: string;
    category: string;
    quantity: number;
    price: number;
  }> {
    if (!Array.isArray(contents)) return [];

    return contents.map(item => {
      if (typeof item === 'object' && item !== null) {
        const typedItem = item as { id: string; quantity: number; item_price: number };
        return {
          item_id: typedItem.id,
          item_name: `Product ${typedItem.id}`,
          category: 'product',
          quantity: typedItem.quantity,
          price: typedItem.item_price,
        };
      }
      return {
        item_id: '',
        item_name: '',
        category: 'product',
        quantity: 0,
        price: 0,
      };
    });
  }

  // Método para adicionar contexto do merchant em todos os eventos
  setMerchantContext(context: MerchantContext) {
    this.pushToDataLayer({
      event: 'merchant_context',
      merchant_id: context.merchant_id,
      merchant_slug: context.merchant_slug,
      store_name: context.store_name,
      channel: context.channel,
    });
  }

  // Método auxiliar para enviar dados para o dataLayer
  private pushToDataLayer(data: Record<string, unknown>) {
    if (window.dataLayer) {
      window.dataLayer.push(data);

      if (this.config.options?.debug) {
        console.log('GTM Event:', data);
      }
    } else {
      console.warn('GTM: dataLayer não está disponível');
    }
  }

  // Método para verificar se o GTM está funcionando corretamente
  checkGTMStatus(): { isLoaded: boolean; isInitialized: boolean; containerId: string } {
    const status = {
      isLoaded: this.isLoaded,
      isInitialized: this.isInitialized,
      containerId: this.config.containerId,
    };

    if (this.config.options?.debug) {
      console.log('GTM Status:', status);

      // Verificar se o script está no DOM
      const script = document.querySelector(
        `script[src*="googletagmanager.com/gtm.js?id=${this.config.containerId}"]`
      );
      console.log('GTM Script no DOM:', !!script);

      // Verificar se o noscript está no DOM
      const noscript = document.querySelector(`noscript[data-gtm-id="${this.config.containerId}"]`);
      console.log('GTM Noscript no DOM:', !!noscript);

      // Verificar dataLayer
      console.log('DataLayer disponível:', !!window.dataLayer);
      console.log('DataLayer length:', window.dataLayer?.length || 0);
    }

    return status;
  }

  loaded() {
    return Promise.resolve(this.isLoaded);
  }

  ready() {
    return Promise.resolve(this.isInitialized && this.isLoaded);
  }
}

// Declaração global para o TypeScript
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}
